import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, X } from 'lucide-react';

import { useGetMyEmotesQuery, usePutMyEmotesMutation, EMOTE_ETICHETTA_MAX, EMOTE_MAX_POSTI } from '@/services/avatarApi';
import { AnteprimaEmote } from '@/components/avatars/AnteprimaEmote';
import { AvatarSkeleton } from '@/components/avatars/AvatarSkeleton';
import { useHaptics } from '@/hooks/useHaptics';
import { useMovimentoRidotto } from '@/hooks/useMovimentoRidotto';
import { Button } from '@/components/common/Button';
import { cn } from '@/lib/cn';
import { accorciataA, conVideo, secondiAnteprima } from '@/lib/avatarEmoteAnteprima';
import { EMOJI_TAVOLO, glifoEmoji } from '@/lib/qgammon/tavolo';
import {
  alternaClip,
  bozzaDaServer,
  bozzeUguali,
  cambiaPosto,
  corpoPut,
  spostaPosto,
  codiceErrore,
  statoHttp,
  type Bozza,
} from '@/lib/avatarEmotes';

/**
 * «Le mie emote» (I-581, ADR-0218): il giocatore sceglie fino a 6 clip dell'avatar in vetrina, per ciascuna l'emoji di ripiego
 * (i 12 codici del tavolo) e, se vuole, un'etichetta. Il set lo tiene Qavatar; il server del gioco lo legge all'ingresso in
 * partita e lo fissa. SOLO col flag `featureAvatarEmotesEnabled` (la rotta non esiste a flag spento).
 *
 * ⚠️ Si sceglie solo una clip la cui versione «emote» e' PRONTA: le altre si vedono, «in preparazione», e non si toccano.
 * ⚠️ Il `PUT` porta la `rev` letta: se e' cambiata nel frattempo (409) si ricarica e lo si dice, senza perdere in silenzio
 * l'altro aggiornamento. ⚠️ Se l'avatar in vetrina e' cambiato, il set di prima non vale piu': la risposta lo dice (`stale`).
 */
export function AvatarEmotes() {
  const { t } = useTranslation('avatars');
  const haptics = useHaptics();
  const ridotto = useMovimentoRidotto();
  const { data, isLoading, isError, error, refetch } = useGetMyEmotesQuery();
  const [salva, { isLoading: inSalvataggio }] = usePutMyEmotesMutation();

  // `null` = nessuna modifica: si mostra il set salvato. Diventa una bozza al primo tocco e torna `null` dopo il salvataggio o
  // dopo un conflitto (si riparte da cio' che c'e' sul server).
  const [bozza, setBozza] = useState<Bozza | null>(null);
  const [esito, setEsito] = useState<'salvato' | 'conflitto' | 'errore' | 'rifiutato' | 'senza-vetrina' | null>(null);

  const base: Bozza = data ? bozzaDaServer(data.slots) : [];
  const corrente = bozza ?? base;
  const modificata = bozza !== null && !bozzeUguali(bozza, base);
  const candidate = data?.candidates ?? [];
  // Le anteprime animate: solo le tessere IN VISTA, al massimo `TETTO_ANTEPRIME_VIDEO` insieme, nessuna col movimento ridotto.
  const [visibili, setVisibili] = useState<ReadonlySet<string>>(() => new Set());
  const onVisibile = useCallback((clipId: string, v: boolean) => {
    setVisibili((prima) => {
      if (prima.has(clipId) === v) return prima;
      const dopo = new Set(prima);
      if (v) dopo.add(clipId);
      else dopo.delete(clipId);
      return dopo;
    });
  }, []);
  const animate = useMemo(() => conVideo(candidate, visibili, ridotto), [candidate, visibili, ridotto]);
  const notEnabled = isError && statoHttp(error) === 503 && codiceErrore(error) === 'emotes_not_enabled';

  const modifica = useCallback(
    (f: (b: Bozza) => Bozza) => {
      setEsito(null);
      setBozza((prima) => f(prima ?? base));
    },
    [base],
  );

  const onSalva = useCallback(async () => {
    if (!data || !bozza) return;
    haptics.impact('light');
    try {
      await salva(corpoPut(bozza, data.rev)).unwrap();
      setBozza(null);
      setEsito('salvato');
    } catch (e) {
      const stato = statoHttp(e);
      if (stato === 409) {
        // La `rev` e' cambiata (un altro dispositivo, un'altra scheda): si riparte da cio' che c'e' ora.
        setBozza(null);
        setEsito('conflitto');
        void refetch();
      } else if (stato === 422 && codiceErrore(e) === 'featured_not_valid') {
        // Nessuna vetrina valida adesso (revocata, non verificabile): il set non ha un avatar a cui appartenere.
        setEsito('senza-vetrina');
        void refetch();
      } else if (stato === 400 || stato === 422) {
        // Una clip che non e' piu' candidata o non ha piu' la versione pronta: si ricarica per vedere cosa e' cambiato.
        setEsito('rifiutato');
        void refetch();
      } else {
        setEsito('errore');
      }
    }
  }, [data, bozza, salva, haptics, refetch]);

  return (
    <div className="flex flex-col gap-4 px-4 pb-24 pt-5" data-testid="avatar-emotes">
      <header>
        {/* I-318, ADR-0190 §1: titolo di sezione fuori dalla vista, `sr-only`; la chiave resta in uso. */}
        <h1 className="sr-only">{t('emotes.title')}</h1>
        <p className="text-sm text-fg-muted">{t('emotes.intro', { max: EMOTE_MAX_POSTI })}</p>
      </header>

      {isLoading && <AvatarSkeleton />}

      {isError && notEnabled && (
        // 503 `emotes_not_enabled`: Qavatar su questo ambiente non ha le emote accese. Riprovare non serve, e il testo non lo promette.
        <div role="status" data-testid="emotes-non-attive" className="rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
          {t('emotes.notEnabled')}
        </div>
      )}
      {isError && !notEnabled && (
        <div role="alert" className="flex flex-col items-center gap-2 rounded-xl bg-bg-secondary p-6 text-center text-sm text-danger">
          <span>{t('emotes.loadFailed')}</span>
          <Button size="sm" onClick={() => void refetch()}>
            {t('emotes.retry')}
          </Button>
        </div>
      )}

      {data && (
        <>
          {data.stale && bozza === null && esito !== 'salvato' && (
            <p role="status" data-testid="emotes-stale" className="rounded-xl bg-bg-secondary p-3 text-sm text-fg">
              {t('emotes.stale')}
            </p>
          )}
          {esito === 'conflitto' && (
            <p role="status" data-testid="emotes-conflitto" className="rounded-xl bg-bg-secondary p-3 text-sm text-fg">
              {t('emotes.conflict')}
            </p>
          )}
          {esito === 'rifiutato' && (
            <p role="alert" data-testid="emotes-rifiutato" className="rounded-xl bg-bg-secondary p-3 text-sm text-danger">
              {t('emotes.rejected')}
            </p>
          )}
          {esito === 'senza-vetrina' && (
            <p role="alert" data-testid="emotes-senza-vetrina" className="rounded-xl bg-bg-secondary p-3 text-sm text-danger">
              {t('emotes.noFeaturedNow')}
            </p>
          )}
          {esito === 'errore' && (
            <p role="alert" data-testid="emotes-errore" className="rounded-xl bg-bg-secondary p-3 text-sm text-danger">
              {t('emotes.saveFailed')}
            </p>
          )}
          {esito === 'salvato' && (
            <p role="status" data-testid="emotes-salvato" className="rounded-xl bg-bg-secondary p-3 text-sm text-fg">
              {t('emotes.saved')}
            </p>
          )}

          {candidate.length === 0 ? (
            <div data-testid="emotes-nessuna-clip" className="rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
              <p>{t(data.featured ? 'emotes.noClips' : 'emotes.noFeatured')}</p>
              <Link to="/avatars" className="mt-3 inline-block rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-brand-fg">
                {t('emotes.goToAvatars')}
              </Link>
            </div>
          ) : (
            <section aria-labelledby="emotes-candidate">
              <h2 id="emotes-candidate" className="mb-2 text-sm font-semibold text-fg">
                {t('emotes.candidates', { n: corrente.length, max: EMOTE_MAX_POSTI })}
              </h2>
              <ul className="grid grid-cols-3 gap-2">
                {candidate.map((c) => {
                  const scelta = corrente.some((p) => p.clipId === c.clipId);
                  const pronta = c.emoteStatus === 'ready';
                  const piena = !scelta && corrente.length >= EMOTE_MAX_POSTI;
                  const spenta = !pronta || piena;
                  return (
                    <li key={c.clipId}>
                      <button
                        type="button"
                        data-testid={`emotes-clip-${c.clipId}`}
                        aria-pressed={scelta}
                        disabled={spenta && !scelta}
                        onClick={() => {
                          haptics.selection();
                          modifica((b) => alternaClip(b, c.clipId, candidate));
                        }}
                        className={cn(
                          'flex h-full w-full flex-col overflow-hidden rounded-xl bg-bg-secondary text-left ring-2 transition-opacity active:opacity-80 disabled:opacity-50',
                          scelta ? 'ring-brand' : 'ring-transparent',
                        )}
                      >
                        <AnteprimaEmote
                          clipId={c.clipId}
                          preview={c.preview}
                          posterUrl={c.posterUrl}
                          anima={animate.has(c.clipId)}
                          onVisibile={onVisibile}
                          className="aspect-square"
                        />
                        <span className="truncate px-2 py-1 text-[11px] text-fg-muted">
                          {!pronta
                            ? c.emoteStatus === 'failed'
                              ? t('emotes.unavailable')
                              : t('emotes.preparing')
                            : (c.label ?? t('emotes.clipUnnamed'))}
                        </span>
                        {pronta && c.preview && (
                          // ADR-0220: il selettore dice al proprietario quanto dura l'anteprima, e se e' piu' corta della clip. La nota
                          // «accorciata» sta su una riga sua e va a capo (`break-words`, mai `truncate`): nessuna etichetta tagliata.
                          <span data-testid={`emotes-durata-${c.clipId}`} className="block break-words px-2 pb-1 text-[11px] leading-tight text-fg-muted">
                            <span className="block">{t('emotes.previewSeconds', { n: secondiAnteprima(c.preview.durationMs) })}</span>{' '}
                            {accorciataA(c.preview, c.durationSec) !== null && (
                              <span data-testid={`emotes-accorciata-${c.clipId}`} className="block">
                                {t('emotes.previewShortened', { n: accorciataA(c.preview, c.durationSec) })}
                              </span>
                            )}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {corrente.length > 0 && (
            <section aria-labelledby="emotes-set">
              <h2 id="emotes-set" className="mb-2 text-sm font-semibold text-fg">
                {t('emotes.setTitle')}
              </h2>
              <p className="mb-2 text-[11px] text-fg-muted">{t('emotes.labelPrivate')}</p>
              <ol className="flex flex-col gap-3">
                {corrente.map((p, i) => {
                  const c = candidate.find((x) => x.clipId === p.clipId);
                  return (
                    <li key={p.clipId} data-testid={`emotes-posto-${p.clipId}`} className="flex flex-col gap-2 rounded-xl bg-bg-secondary p-3">
                      <div className="flex items-center gap-3">
                        <AnteprimaEmote preview={c?.preview} posterUrl={c?.posterUrl} className="h-12 w-12 shrink-0 rounded-lg" />
                        <input
                          type="text"
                          value={p.label}
                          maxLength={EMOTE_ETICHETTA_MAX}
                          placeholder={t('emotes.labelPlaceholder')}
                          aria-label={t('emotes.labelOf', { n: i + 1 })}
                          onChange={(e) => modifica((b) => cambiaPosto(b, p.clipId, { label: e.target.value }))}
                          className="min-w-0 flex-1 rounded-lg bg-bg px-3 py-2 text-sm text-fg"
                        />
                      </div>
                      {!c && (
                        // R4: il posto tiene una clip che non e' piu' fra le candidate (cancellata, versione «emote» sparita): il PUT la
                        // rifiuterebbe. Lo si dice QUI, sul posto, e si offre di toglierla.
                        <p role="status" data-testid={`emotes-posto-perso-${p.clipId}`} className="text-[11px] text-danger">
                          {t('emotes.slotGone')}
                        </p>
                      )}
                      {/* R2: le tre azioni in una riga, a 44 px di passo (icona da 40 px + 4 di spazio): le aree di tocco non si sovrappongono. */}
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="icona" size={40} aria-label={t('emotes.moveUp')} disabled={i === 0} onClick={() => modifica((b) => spostaPosto(b, p.clipId, -1))}>
                          <ArrowUp aria-hidden className="h-5 w-5" />
                        </Button>
                        <Button variant="icona" size={40} aria-label={t('emotes.moveDown')} disabled={i === corrente.length - 1} onClick={() => modifica((b) => spostaPosto(b, p.clipId, 1))}>
                          <ArrowDown aria-hidden className="h-5 w-5" />
                        </Button>
                        <Button variant="icona" size={40} aria-label={t('emotes.remove')} onClick={() => modifica((b) => alternaClip(b, p.clipId, candidate))}>
                          <X aria-hidden className="h-5 w-5" />
                        </Button>
                      </div>
                      <div role="group" aria-label={t('emotes.fallbackOf', { n: i + 1 })} className="grid grid-cols-6 gap-1">
                        {EMOJI_TAVOLO.map(([codice]) => (
                          <Button
                            key={codice}
                            variant="scelta"
                            forma="tessera"
                            tono="incavo"
                            pressed={p.fallback === codice}
                            aria-label={t(`tavolo.emoji.nome.${codice}`, { ns: 'backgammon', defaultValue: codice })}
                            data-testid={`emotes-ripiego-${p.clipId}-${codice}`}
                            onClick={() => modifica((b) => cambiaPosto(b, p.clipId, { fallback: codice }))}
                            className="text-xl"
                          >
                            {glifoEmoji(codice)}
                          </Button>
                        ))}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </section>
          )}

          {candidate.length > 0 && (
            <Button fullWidth data-testid="emotes-salva" disabled={!modificata || inSalvataggio} onClick={() => void onSalva()}>
              {inSalvataggio ? t('emotes.saving') : t('emotes.save')}
            </Button>
          )}
        </>
      )}
    </div>
  );
}
